import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Selector de color como el de Windows, igual en Android, iPhone y compu: un ARCOÍRIS en 2D (tono de izquierda a
 * derecha, saturación de arriba abajo: hasta abajo es blanco), una barra de al lado para qué tan OSCURO, y —sólo
 * donde tiene sentido (`alfa`)— otra de TRANSPARENCIA. Abajo: los colores de la clínica, muestras, Hex, RGB, CMYK y
 * el gotero donde el navegador lo tiene. Existe porque el `<input type="color">` lo pinta cada sistema a su manera
 * (en Android es una cuadrícula de unos cuantos colores).
 *
 * El dato es "#rrggbb" (o "#rrggbbaa" con `alfa` y algo de transparencia). `onChange` = cada cambio (vista previa
 * en vivo); `onCommit` = al aplicar. Cancelar o Escape regresan al color con el que se abrió (a `null` si no había);
 * tocar fuera deja lo que se ve: lo que ya pinta la vista previa no se pierde.
 */
const MUESTRAS: { hex: string; t: string }[] = [
  { hex: '#ac996c', t: 'Dorado de la marca' }, { hex: '#8b7355', t: 'Dorado oscuro' }, { hex: '#8e1a5f', t: 'Magenta de la marca' },
  { hex: '#d4af37', t: 'Oro' }, { hex: '#b76e79', t: 'Oro rosa' }, { hex: '#f7e7ce', t: 'Champaña' }, { hex: '#cd7f32', t: 'Bronce' },
  { hex: '#a8a9ad', t: 'Plata' }, { hex: '#e5e4e2', t: 'Platino' }, { hex: '#1f2937', t: 'Grafito' }, { hex: '#e5484d', t: 'Rojo' },
  { hex: '#d6409f', t: 'Rosa' }, { hex: '#8e4ec6', t: 'Morado' }, { hex: '#3e63dd', t: 'Azul' }, { hex: '#12a594', t: 'Turquesa' },
  { hex: '#065f46', t: 'Verde' },
];

type Hsv = { h: number; s: number; v: number };
/** '#rrggbb' o, con `alfa`, '#rrggbbaa' ('ff' se quita: sin transparencia se guarda de 6). */
export function normalizaHex(v: string, alfa = false): string | null {
  const m = /^#?([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(String(v || '').trim());
  if (!m || (m[2] && !alfa)) return null;
  const a = (m[2] || 'ff').toLowerCase();
  return `#${m[1].toLowerCase()}${a === 'ff' ? '' : a}`;
}
export function hexARgb(hex: string): { r: number; g: number; b: number } {
  const h = (normalizaHex(hex, true) || '#000000').slice(0, 7);
  return { r: parseInt(h.slice(1, 3), 16), g: parseInt(h.slice(3, 5), 16), b: parseInt(h.slice(5, 7), 16) };
}
export function rgbAHex(r: number, g: number, b: number): string {
  const c = (n: number) => Math.max(0, Math.min(255, Math.round(Number(n) || 0))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}
/** RGB → CMYK en porcentajes 0–100 (lo que pide imprenta). */
export function rgbACmyk(r: number, g: number, b: number): { c: number; m: number; y: number; k: number } {
  const R = r / 255, G = g / 255, B = b / 255;
  const k = 1 - Math.max(R, G, B);
  if (k >= 1) return { c: 0, m: 0, y: 0, k: 100 };
  const f = (x: number) => Math.round(((1 - x - k) / (1 - k)) * 100);
  return { c: f(R), m: f(G), y: f(B), k: Math.round(k * 100) };
}
export function cmykAHex(c: number, m: number, y: number, k: number): string {
  const p = (n: number) => Math.max(0, Math.min(100, Number(n) || 0)) / 100;
  const K = p(k);
  return rgbAHex(255 * (1 - p(c)) * (1 - K), 255 * (1 - p(m)) * (1 - K), 255 * (1 - p(y)) * (1 - K));
}
export function hexAHsv(hex: string): Hsv {
  const { r, g, b } = hexARgb(hex);
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B), d = max - min;
  let h = 0;
  if (d) { if (max === R) h = ((G - B) / d) % 6; else if (max === G) h = (B - R) / d + 2; else h = (R - G) / d + 4; h *= 60; if (h < 0) h += 360; }
  return { h, s: max ? d / max : 0, v: max };
}
export function hsvAHex(h: number, s: number, v: number): string {
  h = ((h % 360) + 360) % 360;
  const c = v * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = v - c;
  let R = 0, G = 0, B = 0;
  if (h < 60) [R, G, B] = [c, x, 0]; else if (h < 120) [R, G, B] = [x, c, 0]; else if (h < 180) [R, G, B] = [0, c, x];
  else if (h < 240) [R, G, B] = [0, x, c]; else if (h < 300) [R, G, B] = [x, 0, c]; else [R, G, B] = [c, 0, x];
  return rgbAHex((R + m) * 255, (G + m) * 255, (B + m) * 255);
}
/** Un color de 6 más su opacidad (0–1) → '#rrggbb' o '#rrggbbaa'. */
function conAlfa(hex6: string, a: number): string {
  const n = Math.max(0, Math.min(255, Math.round(a * 255)));
  return n >= 255 ? hex6 : hex6 + n.toString(16).padStart(2, '0');
}
function alfaDe(hex: string): number { const h = normalizaHex(hex, true); return h && h.length === 9 ? parseInt(h.slice(7), 16) / 255 : 1; }
const tieneGotero = typeof window !== 'undefined' && 'EyeDropper' in window;
const ANCHO = 292;
const ARCOIRIS = 'linear-gradient(to right, #f00 0%, #ff0 16.67%, #0f0 33.33%, #0ff 50%, #00f 66.67%, #f0f 83.33%, #f00 100%)';
/** Fondo de cuadritos: lo que se ve detrás de un color transparente. */
const CUADRITOS = 'repeating-conic-gradient(#d8d4cc 0% 25%, #ffffff 0% 50%) 0 0 / 10px 10px';

const CSS = `
.dcp-num { margin: 0; padding: 5px 6px; font-size: 13px; border-radius: 8px; width: 100%; text-align: center; }
.dcp-lbl { font-size: 11px; font-weight: 700; color: var(--ink-soft); display: grid; gap: 2px; text-transform: uppercase; letter-spacing: .04em; }
.dcp-sw { width: 26px; height: 26px; border-radius: 7px; padding: 0; cursor: pointer; border: 1px solid rgba(0,0,0,.15); box-shadow: none; }
.dcp-sw[aria-pressed="true"] { outline: 2px solid var(--gold-dark); outline-offset: 2px; }
.dcp-sw:hover { transform: none; box-shadow: none; }
.dcp-zona:focus-visible { outline: 3px solid var(--gold-medium); outline-offset: 2px; }
.dcp-barra { position: relative; width: 26px; border-radius: 8px; cursor: ns-resize; touch-action: none; user-select: none; flex-shrink: 0; border: 1px solid rgba(0,0,0,.12); }
.dcp-barra > i { position: absolute; left: -4px; right: -4px; height: 10px; margin-top: -5px; border-radius: 5px; border: 2px solid #fff; box-shadow: 0 0 0 1px rgba(0,0,0,.55), 0 1px 4px rgba(0,0,0,.3); pointer-events: none; }
.dcp-antes { border: 0; padding: 0; border-radius: 0; box-shadow: none; cursor: pointer; }
.dcp-antes:hover { transform: none; box-shadow: none; }
`;

/** Arrastrar con mouse o dedo sobre una zona: devuelve la posición 0–1 en x y en y. Las orillas llevan 4 px de
 *  margen: con el dedo no se atina al último píxel, y sin margen el negro puro o la luz completa no se alcanzan. */
function arrastre(el: () => HTMLElement | null, fn: (x: number, y: number) => void) {
  const M = 4;
  const eje = (c: number, ini: number, largo: number) => Math.max(0, Math.min(1, (c - ini - M) / Math.max(1, largo - 2 * M)));
  const desde = (cx: number, cy: number) => {
    const e = el(); if (!e) return;
    const r = e.getBoundingClientRect();
    fn(eje(cx, r.left, r.width), eje(cy, r.top, r.height));
  };
  return {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => { e.currentTarget.setPointerCapture?.(e.pointerId); desde(e.clientX, e.clientY); },
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => { if (e.buttons & 1) desde(e.clientX, e.clientY); },
  };
}

export function ColorPicker({ value, vacio, onChange, onCommit, etiqueta, sugeridos, compacto, deshabilitado, alfa, attrs }: {
  value: string | null | undefined;
  /** El color que se enseña cuando `value` está vacío (p. ej. el del nivel). */
  vacio?: string | null;
  /** Cada cambio (vista previa en vivo). Al cancelar regresa lo de antes: `null` si estaba vacío. */
  onChange?: (hex: string | null) => void;
  /** Al aplicar (o al tocar fuera con un color distinto). */
  onCommit: (hex: string) => void;
  /** Qué color es ("Color del diseño"): va en el botón y en el título del panel. */
  etiqueta: string;
  /** Colores que ya son de la clínica (la marca, los niveles), arriba de las muestras. */
  sugeridos?: { hex: string; t: string }[];
  /** Sólo el círculo, sin el código (para una tabla). */
  compacto?: boolean;
  deshabilitado?: boolean;
  /** Con barra de transparencia (el dato puede salir '#rrggbbaa'). */
  alfa?: boolean;
  /** Atributos extra del botón (p. ej. data-color). */
  attrs?: Record<string, string>;
}) {
  const crudo = normalizaHex(value || '', !!alfa);
  const actual = crudo || normalizaHex(vacio || '', !!alfa) || '#ac996c';
  const [abierto, setAbierto] = useState(false);
  const [hsv, setHsv] = useState<Hsv>(() => hexAHsv(actual));
  const [a, setA] = useState(() => alfaDe(actual));
  const [hexTxt, setHexTxt] = useState(actual);
  const inicial = useRef<{ hex: string; crudo: string | null }>({ hex: actual, crudo });
  const boton = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const espectro = useRef<HTMLDivElement>(null);
  const brillo = useRef<HTMLDivElement>(null);
  const transp = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const hex6 = hsvAHex(hsv.h, hsv.s, hsv.v);
  const hex = alfa ? conAlfa(hex6, a) : hex6;
  const puro = hsvAHex(hsv.h, hsv.s, 1); // el color del arcoíris en ese punto, con toda la luz

  // Mientras está abierto, el color lo lleva el panel: `value` regresa con cada cambio en vivo y volver a leerlo
  // perdería el tono al pasar por un gris o por el negro (ahí no hay tono ni saturación que leer).
  useEffect(() => { if (!abierto) { setHsv(hexAHsv(actual)); setA(alfaDe(actual)); setHexTxt(actual); } }, [actual, abierto]);

  // El panel va en un PORTAL con posición fija: como hijo del botón lo recortaría cualquier tarjeta o tabla con
  // overflow (la de niveles se desplaza de lado). Si no cabe abajo se abre arriba, y nunca pasa de la ventana.
  const coloca = () => {
    const b = boton.current?.getBoundingClientRect();
    if (!b) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    const alto = pop.current?.offsetHeight || 460, ancho = Math.min(ANCHO, vw - 16);
    const abajo = b.bottom + 6;
    const top = abajo + alto <= vh - 8 ? abajo : Math.max(8, Math.min(abajo, b.top - 6 - alto));
    const left = Math.max(8, Math.min(b.left, vw - ancho - 8));
    setPos({ top, left });
  };
  useLayoutEffect(() => {
    if (!abierto) { setPos(null); return; }
    coloca();
    const f = () => coloca();
    window.addEventListener('resize', f); window.addEventListener('scroll', f, true);
    return () => { window.removeEventListener('resize', f); window.removeEventListener('scroll', f, true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  const cerrar = (confirmar: boolean) => {
    if (confirmar) { if (hex !== inicial.current.hex) onCommit(hex); }
    else if (hex !== inicial.current.hex) onChange?.(inicial.current.crudo);
    setAbierto(false);
  };
  const cerrarRef = useRef(cerrar); cerrarRef.current = cerrar;
  useEffect(() => {
    if (!abierto) return;
    pop.current?.focus({ preventScroll: true });
    const fuera = (e: PointerEvent) => {
      const t = e.target as Node;
      if (boton.current?.contains(t) || pop.current?.contains(t)) return;
      cerrarRef.current(true);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation(); cerrarRef.current(false); boton.current?.focus();
    };
    document.addEventListener('pointerdown', fuera, true);
    window.addEventListener('keydown', tecla, true);
    return () => { document.removeEventListener('pointerdown', fuera, true); window.removeEventListener('keydown', tecla, true); };
  }, [abierto]);

  const abrir = () => {
    if (abierto) { cerrar(true); return; }
    inicial.current = { hex: actual, crudo }; setHsv(hexAHsv(actual)); setA(alfaDe(actual)); setHexTxt(actual); setAbierto(true);
  };

  // Un solo punto de verdad: arcoíris, barras, rgb, cmyk, muestras y gotero terminan aquí.
  const poner = (n: Hsv, na = a) => {
    setHsv(n); setA(na);
    const h = alfa ? conAlfa(hsvAHex(n.h, n.s, n.v), na) : hsvAHex(n.h, n.s, n.v);
    setHexTxt(h); onChange?.(h);
  };
  /** Sin tono que leer (gris) o sin luz (negro) se conserva lo que había, para que el punto no brinque. */
  const hsvDesde = (h: string): Hsv => { const n = hexAHsv(h); return n.v === 0 ? { ...hsv, v: 0 } : n.s === 0 ? { h: hsv.h, s: 0, v: n.v } : n; };
  /** Una muestra, RGB, CMYK o el gotero: el color cambia y la transparencia se queda. */
  const ponerHex = (h: string) => poner(hsvDesde(h));

  const rgb = hexARgb(hex6);
  const cmyk = rgbACmyk(rgb.r, rgb.g, rgb.b);
  // Los números guardan su TEXTO aparte: con `value={rgb.r}` un campo controlado no se puede vaciar (sale "0")
  // y en Android lo tecleado se queda con el cero al frente ("0125").
  const [numTxt, setNumTxt] = useState<Record<string, string>>({});
  useEffect(() => { setNumTxt({}); }, [hex]);
  const txtDe = (k: string, v: number) => (numTxt[k] !== undefined ? numTxt[k] : String(v));
  const ponNum = (k: string, crudoTxt: string, max: number, aplicar: (n: number) => void) => {
    const limpio = crudoTxt.replace(/[^0-9]/g, '').slice(0, 3);
    if (limpio === '') { setNumTxt((t) => ({ ...t, [k]: '' })); return; }
    const n = Math.min(max, parseInt(limpio, 10));
    setNumTxt((t) => ({ ...t, [k]: String(n) }));
    aplicar(n);
  };
  const ponRgb = (k: 'r' | 'g' | 'b', v: number) => { const n = { ...rgb, [k]: v }; ponerHex(rgbAHex(n.r, n.g, n.b)); };
  const ponCmyk = (k: 'c' | 'm' | 'y' | 'k', v: number) => { const n = { ...cmyk, [k]: v }; ponerHex(cmykAHex(n.c, n.m, n.y, n.k)); };

  // En el arcoíris siempre se ve el color con luz: si estaba en negro, tocarlo le regresa la luz.
  const enEspectro = arrastre(() => espectro.current, (x, y) => poner({ h: x * 360, s: 1 - y, v: hsv.v || 1 }));
  const enBrillo = arrastre(() => brillo.current, (_x, y) => poner({ ...hsv, v: 1 - y }));
  const enTransp = arrastre(() => transp.current, (_x, y) => poner(hsv, 1 - y));
  const flechas = (e: React.KeyboardEvent, mov: Record<string, () => void>) => { const f = mov[e.key]; if (f) { e.preventDefault(); f(); } };
  const lim = (n: number) => Math.max(0, Math.min(1, n));
  const pasoT = (e: React.KeyboardEvent) => (e.shiftKey ? 15 : 3);
  const pasoN = (e: React.KeyboardEvent) => (e.shiftKey ? 0.1 : 0.02);

  async function gotero() {
    try {
      const Ey = (window as unknown as { EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> } }).EyeDropper;
      const r = await new Ey().open();
      const h = normalizaHex(r.sRGBHex); if (h) ponerHex(h);
    } catch { /* se canceló */ }
  }

  const mostrado = abierto ? hex : actual;
  const conCuadritos = (c: string) => (alfa ? `linear-gradient(${c}, ${c}), ${CUADRITOS}` : c);
  const sug = (sugeridos || []).map((s) => ({ ...s, hex: normalizaHex(s.hex) })).filter((s): s is { hex: string; t: string } => !!s.hex)
    .filter((s, i, arr) => arr.findIndex((o) => o.hex === s.hex) === i);
  const fila = (lista: { hex: string; t: string }[], dato: string) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 26px)', gap: 6 }}>
      {lista.map((m) => (
        <button key={dato + m.hex} type="button" className="dcp-sw" title={`${m.t} · ${m.hex}`} aria-label={m.t} aria-pressed={hex6 === m.hex}
          data-muestra={m.hex} onClick={() => ponerHex(m.hex)} style={{ background: m.hex }} />
      ))}
    </div>
  );
  const pct = (n: number) => `${Math.round(n * 100)} %`;

  return (
    <>
      <button ref={boton} type="button" disabled={deshabilitado} onClick={abrir} aria-haspopup="dialog" aria-expanded={abierto}
        aria-label={`${etiqueta}: ${mostrado}`} title={deshabilitado ? undefined : `${etiqueta} · toca para cambiarlo`} {...(attrs || {})}
        style={compacto
          ? { width: 30, height: 30, padding: 0, borderRadius: '50%', background: conCuadritos(mostrado), border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,.2)', flexShrink: 0 }
          : { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 12px 4px 4px', borderRadius: 999, background: '#fff', border: '1px solid var(--border)', fontSize: 13, color: 'var(--ink)', boxShadow: 'none' }}>
        {!compacto && <>
          <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: '50%', background: conCuadritos(mostrado), border: '1px solid rgba(0,0,0,.15)', flexShrink: 0 }} />
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '.02em' }}>{mostrado}</span>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" style={{ color: 'var(--gold-dark)' }}><path d="m6 9 6 6 6-6" /></svg>
        </>}
      </button>
      {abierto && createPortal(
        <div ref={pop} role="dialog" aria-label={etiqueta} tabIndex={-1} data-color-picker
          style={{ position: 'fixed', zIndex: 1200, top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: `min(${ANCHO}px, calc(100vw - 16px))`,
            maxHeight: 'calc(100vh - 16px)', overflowY: 'auto', boxSizing: 'border-box', background: '#fff', border: '1px solid var(--border)',
            borderRadius: 16, padding: 12, boxShadow: '0 14px 40px rgba(40, 33, 40, .22)', outline: 'none', fontFamily: 'inherit', color: 'var(--ink)' }}>
          <style>{CSS}</style>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gold-dark)', marginBottom: 8 }}>{etiqueta}</div>
          <div style={{ display: 'flex', gap: 12, height: 168 }}>
            {/* El arcoíris: tono de izquierda a derecha, saturación de arriba (vivo) abajo (blanco) — como Windows */}
            <div ref={espectro} className="dcp-zona" role="slider" tabIndex={0} aria-label="Color: tono y saturación" aria-valuetext={puro} data-espectro
              {...enEspectro}
              onKeyDown={(e) => flechas(e, {
                ArrowLeft: () => poner({ ...hsv, h: (hsv.h - pasoT(e) + 360) % 360 }), ArrowRight: () => poner({ ...hsv, h: (hsv.h + pasoT(e)) % 360 }),
                ArrowUp: () => poner({ ...hsv, s: lim(hsv.s + pasoN(e)) }), ArrowDown: () => poner({ ...hsv, s: lim(hsv.s - pasoN(e)) }),
              })}
              style={{ position: 'relative', flex: 1, minWidth: 0, borderRadius: 10, cursor: 'crosshair', userSelect: 'none', touchAction: 'none',
                background: `linear-gradient(to bottom, rgba(255,255,255,0), #fff), ${ARCOIRIS}`, border: '1px solid rgba(0,0,0,.12)' }}>
              <span style={{ position: 'absolute', left: `${(hsv.h / 360) * 100}%`, top: `${(1 - hsv.s) * 100}%`, width: 18, height: 18, marginLeft: -9, marginTop: -9,
                borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,.6), 0 1px 4px rgba(0,0,0,.3)', background: puro, pointerEvents: 'none' }} />
            </div>
            {/* Qué tan oscuro: arriba el color con toda la luz, abajo negro */}
            <div ref={brillo} className="dcp-barra dcp-zona" role="slider" tabIndex={0} aria-label="Qué tan oscuro" aria-orientation="vertical"
              aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(hsv.v * 100)} title={`Luz: ${pct(hsv.v)}`} data-brillo
              {...enBrillo}
              onKeyDown={(e) => flechas(e, { ArrowUp: () => poner({ ...hsv, v: lim(hsv.v + pasoN(e)) }), ArrowDown: () => poner({ ...hsv, v: lim(hsv.v - pasoN(e)) }) })}
              style={{ background: `linear-gradient(to bottom, ${puro}, #000)` }}>
              <i style={{ top: `${(1 - hsv.v) * 100}%`, background: hex6 }} />
            </div>
            {alfa && (
              <div ref={transp} className="dcp-barra dcp-zona" role="slider" tabIndex={0} aria-label="Transparencia" aria-orientation="vertical"
                aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((1 - a) * 100)} title={`Transparencia: ${pct(1 - a)}`} data-transparencia
                {...enTransp}
                onKeyDown={(e) => flechas(e, { ArrowUp: () => poner(hsv, lim(a + pasoN(e))), ArrowDown: () => poner(hsv, lim(a - pasoN(e))) })}
                style={{ background: `linear-gradient(to bottom, ${hex6}, ${hex6}00), ${CUADRITOS}` }}>
                <i style={{ top: `${(1 - a) * 100}%`, background: conCuadritos(hex) }} />
              </div>
            )}
          </div>
          {/* Debajo de cada cosa, qué es: el arcoíris a la izquierda, cada barra con su nombre y su valor */}
          <div style={{ display: 'flex', gap: 12, fontSize: 10.5, color: 'var(--ink-soft)', margin: '5px 0 10px', lineHeight: 1.25 }} data-lectura>
            <span style={{ flex: 1, minWidth: 0 }}>Arrastra en el arcoíris</span>
            <span style={{ width: 26, display: 'flex', justifyContent: 'center', whiteSpace: 'nowrap', textAlign: 'center' }}><span><b style={{ display: 'block', fontWeight: 700 }}>Luz</b> {pct(hsv.v)}</span></span>
            {alfa && <span style={{ width: 26, display: 'flex', justifyContent: 'center', whiteSpace: 'nowrap', textAlign: 'center' }}><span><b style={{ display: 'block', fontWeight: 700 }}>Transp.</b> {pct(1 - a)}</span></span>}
          </div>
          {sug.length > 0 && <>
            <div className="dcp-lbl" style={{ marginBottom: 4 }}>Los de la clínica</div>
            {fila(sug, 's')}
            <div style={{ height: 8 }} />
          </>}
          {fila(MUESTRAS, 'm')}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', margin: '12px 0 8px' }}>
            {/* Antes | ahora, como el cuadro de Windows. Tocar "antes" regresa a ese color. */}
            <div style={{ display: 'flex', width: 52, height: 38, borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(0,0,0,.15)', flexShrink: 0 }}>
              <button type="button" className="dcp-antes" title={`Antes: ${inicial.current.hex} (toca para regresar a ése)`} aria-label="Regresar al color de antes" data-antes
                onClick={() => poner(hsvDesde(inicial.current.hex), alfaDe(inicial.current.hex))} style={{ flex: 1, background: conCuadritos(inicial.current.hex) }} />
              <span title={`Ahora: ${hex}`} style={{ flex: 1, background: conCuadritos(hex) }} />
            </div>
            <label className="dcp-lbl" style={{ flex: 1, minWidth: 0 }}>Hex
              <input className="dcp-num" value={hexTxt} maxLength={alfa ? 9 : 7} spellCheck={false} autoCapitalize="off" autoComplete="off" data-hex
                style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', textAlign: 'left' }}
                onChange={(e) => {
                  setHexTxt(e.target.value);
                  const h = normalizaHex(e.target.value, !!alfa);
                  // Lo tecleado manda completo: "#rrggbb" es sin transparencia, "#rrggbbaa" trae la suya.
                  if (h) { setHsv(hsvDesde(h)); setA(alfaDe(h)); onChange?.(h); }
                }}
                onBlur={() => setHexTxt(hex)} />
            </label>
            {tieneGotero && (
              <button type="button" className="btn-ghost" title="Tomar el color de cualquier punto de la pantalla" onClick={gotero} style={{ padding: '6px 10px', fontSize: 12.5 }}>Gotero</button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 6 }}>
            {(['r', 'g', 'b'] as const).map((k) => (
              <label key={k} className="dcp-lbl">{k.toUpperCase()}
                <input className="dcp-num" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={3} value={txtDe(k, rgb[k])} data-rgb={k}
                  onChange={(e) => ponNum(k, e.target.value, 255, (n) => ponRgb(k, n))} onBlur={() => setNumTxt({})} />
              </label>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 12 }}>
            {(['c', 'm', 'y', 'k'] as const).map((k) => (
              <label key={k} className="dcp-lbl">{k.toUpperCase()} %
                <input className="dcp-num" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={3} value={txtDe(k, cmyk[k])} data-cmyk={k}
                  onChange={(e) => ponNum(k, e.target.value, 100, (n) => ponCmyk(k, n))} onBlur={() => setNumTxt({})} />
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-ghost" style={{ padding: '7px 16px', fontSize: 13 }} data-cancelar onClick={() => { cerrar(false); boton.current?.focus(); }}>Cancelar</button>
            <button type="button" className="btn-primary" style={{ padding: '7px 16px', fontSize: 13 }} data-aplicar onClick={() => { cerrar(true); boton.current?.focus(); }}>Aplicar</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
